import React, { useState } from 'react';
import CodeExample from './CodeExample';
import './EndpointCard.css';

/**
 * EndpointCard Component
 * Displays API endpoint information (method, path, description, parameters, test scripts)
 */
const EndpointCard = ({ 
  method = 'GET', 
  path = '', 
  fullUrl = '',
  description = '', 
  parameters = [],
  required = [],
  note = '',
  curlExample = '',
  requestBody = null
}) => {
  const methodClass = method.toLowerCase();
  const [showTestScript, setShowTestScript] = useState(false);

  // Generate cURL example if not provided
  const generateCurlExample = () => {
    if (curlExample) return curlExample;
    
    let curl = `curl -X ${method} "${fullUrl}`;
    
    if (method === 'GET' && parameters.some(p => p.name === 'query' || p.name === 'splitId' || p.name === 'limit')) {
      const queryParams = [];
      parameters.forEach(param => {
        if (param.name === 'query') queryParams.push('query=john');
        if (param.name === 'splitId') queryParams.push('splitId=split_123');
        if (param.name === 'limit') queryParams.push('limit=20');
      });
      if (queryParams.length > 0) {
        curl += `?${queryParams.join('&')}`;
      }
    }
    
    curl += `" \\\n  -H "Authorization: Bearer YOUR_API_KEY"`;
    
    if (method !== 'GET') {
      curl += ` \\\n  -H "Content-Type: application/json"`;
    }
    
    if (method === 'POST' && requestBody) {
      curl += ` \\\n  -d '${JSON.stringify(requestBody, null, 2).replace(/\n/g, ' ')}'`;
    } else if (method === 'POST') {
      // Generate minimal request body from required parameters
      const body = {};
      required.forEach(paramName => {
        const param = parameters.find(p => p.name === paramName);
        if (param) {
          if (param.type === 'string') body[paramName] = 'example_value';
          else if (param.type === 'number') body[paramName] = 0;
          else if (param.type === 'array') body[paramName] = [];
          else if (param.type === 'boolean') body[paramName] = true;
          else body[paramName] = null;
        }
      });
      if (Object.keys(body).length > 0) {
        curl += ` \\\n  -d '${JSON.stringify(body)}'`;
      }
    }
    
    return curl;
  };

  const curlCmd = generateCurlExample();

  return (
    <div className="endpoint-card">
      <div className="endpoint-header">
        <span className={`endpoint-method endpoint-method-${methodClass}`}>
          {method}
        </span>
        <code className="endpoint-path">{path}</code>
      </div>
      {fullUrl && (
        <div className="endpoint-full-url">
          <strong>Full URL:</strong> <code>{fullUrl}</code>
        </div>
      )}
      {description && (
        <p className="endpoint-description">{description}</p>
      )}
      {note && (
        <p className="endpoint-note"><em>{note}</em></p>
      )}
      {parameters.length > 0 && (
        <div className="endpoint-parameters">
          <h4 className="endpoint-parameters-title">Parameters</h4>
          <table className="endpoint-parameters-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {parameters.map((param, index) => (
                <tr key={index}>
                  <td><code>{param.name}</code></td>
                  <td>{param.type}</td>
                  <td>{required.includes(param.name) ? 'Yes' : 'No'}</td>
                  <td>{param.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="endpoint-test-script">
        <button 
          className="endpoint-test-toggle"
          onClick={() => setShowTestScript(!showTestScript)}
        >
          {showTestScript ? '▼' : '▶'} Test Script (cURL)
        </button>
        {showTestScript && (
          <div className="endpoint-test-content">
            <CodeExample
              code={curlCmd}
              language="bash"
              title="cURL"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EndpointCard;
